import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, UseMutationOptions, useQuery } from '@tanstack/react-query';

import {
  GameCreateSnapshotRequest,
  GameSnapshot,
  GameSnapshotRequest,
  GameState,
  GameStatus,
  GameType,
  SnapshotListQuery,
  SnapshotListResponse,
} from '~/server/src/dtos';
import type { Player } from '~/server/src/tmmodel';

import { getFn, MessageResponse, postFn, queryClient } from './api';

/** Filters for GET /games/history (all optional). */
export interface GameHistoryFilters {
  gameType?: GameType;
  dateFrom?: string;
  dateTo?: string;
  playerId?: string;
  status?: GameStatus;
}

/** One game in the history list (no snapshotId). */
export interface GameHistoryEntry {
  gameId: string;
  timeCreated: string;
  gameType: GameType;
  status: GameStatus;
  name?: string;
  players: Player[];
}

export const games = createQueryKeys('games', {
  all: null,
  history: (filters: GameHistoryFilters | undefined) => ({
    queryKey: ['history', filters ?? undefined],
  }),
  detail: (gameId: string) => ({ queryKey: [gameId] }),
});

export type SnapshotListFilters = SnapshotListQuery;

export const snapshots = createQueryKeys('snapshots', {
  all: null,
  list: (filters: SnapshotListFilters | undefined) => ({
    queryKey: ['list', filters ?? {}],
  }),
  detail: (snapshotId: string) => ({ queryKey: [snapshotId] }),
});

export const getGames = `/games`;
export const getGamesHistory = `${getGames}/history`;
export const getGame = (gameId: string): string => `${getGames}/${gameId}`;
export const postCreateSnapshot = (gameId: string): string =>
  `${getGames}/${gameId}/createSnapshot`;
export const getSnapshots = `${getGames}/snapshots`;
export const getSnapshot = (snapshotId: string): string => `${getSnapshots}/${snapshotId}`;
export const postCustomSnapshot = getSnapshots;

export const useGames = (
  options?: Omit<Parameters<typeof useQuery<GameState[]>>[0], 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GameState[]>({
    ...games.all,
    queryFn: ({ signal }) => getFn(getGames, undefined, undefined, { signal }),
    ...options,
  });
};

export const useGameHistory = (
  filters?: GameHistoryFilters,
  options?: Omit<Parameters<typeof useQuery<GameHistoryEntry[]>>[0], 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GameHistoryEntry[]>({
    ...games.history(filters),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (filters?.gameType != null) params.set('gameType', filters.gameType);
      if (filters?.dateFrom != null) params.set('dateFrom', filters.dateFrom);
      if (filters?.dateTo != null) params.set('dateTo', filters.dateTo);
      if (filters?.playerId != null) params.set('playerId', filters.playerId);
      if (filters?.status != null) params.set('status', filters.status);
      const qs = params.toString();
      return getFn(qs ? `${getGamesHistory}?${qs}` : getGamesHistory, undefined, undefined, {
        signal,
      });
    },
    ...options,
  });
};

export const useGame = (
  gameId: string,
  options?: Omit<Parameters<typeof useQuery<GameState>>[0], 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GameState>({
    ...games.detail(gameId),
    queryFn: ({ signal }) => getFn(getGame(gameId), undefined, undefined, { signal }),
    ...options,
    enabled: !!gameId && (options?.enabled === undefined || options.enabled === true),
  });
};

export const useSnapshots = (
  filters?: SnapshotListFilters,
  options?: Omit<Parameters<typeof useQuery<SnapshotListResponse>>[0], 'queryKey' | 'queryFn'>,
) => {
  return useQuery<SnapshotListResponse>({
    ...snapshots.list(filters),
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (filters?.page != null) params.set('page', String(filters.page));
      if (filters?.pageSize != null) params.set('pageSize', String(filters.pageSize));
      if (filters?.search?.trim()) params.set('search', filters.search.trim());
      if (filters?.sortBy != null) params.set('sortBy', filters.sortBy);
      if (filters?.sortDir != null) params.set('sortDir', filters.sortDir);
      if (filters?.source != null) params.set('source', filters.source);
      const qs = params.toString();
      return getFn(qs ? `${getSnapshots}?${qs}` : getSnapshots, undefined, undefined, { signal });
    },
    ...options,
  });
};

export const useSnapshot = (
  snapshotId = '',
  options?: Omit<Parameters<typeof useQuery<GameSnapshot>>[0], 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GameSnapshot>({
    ...snapshots.detail(snapshotId),
    queryFn: ({ signal }) => getFn(getSnapshot(snapshotId), undefined, undefined, { signal }),
    ...options,
    enabled: !!snapshotId && (options?.enabled === undefined || options.enabled === true),
    // Don't retry on 401/403 errors for snapshots - replay should work without auth
    retry: (failureCount, error) => {
      const status = (error as { status?: number })?.status;
      if (status === 401 || status === 403) {
        return false; // Don't retry auth errors
      }
      // Use default retry logic for other errors
      return failureCount < 3;
    },
  });
};

export const useCreateSnapshot = (
  gameId?: string,
  options?: Parameters<typeof useMutation<MessageResponse, unknown, GameCreateSnapshotRequest>>[0],
) => {
  return useMutation<MessageResponse, unknown, GameCreateSnapshotRequest>({
    mutationFn: (data) => postFn(postCreateSnapshot(gameId ?? ''), data),
    ...options,
    onSuccess: (...response) => {
      queryClient.invalidateQueries({ queryKey: snapshots.all.queryKey });
      queryClient.invalidateQueries({ queryKey: ['snapshots', 'list'] });
      options?.onSuccess?.(...response);
    },
  });
};

export const useCreateCustomSnapshot = (
  options?: Parameters<typeof useMutation<MessageResponse, unknown, GameSnapshotRequest>>[0],
) => {
  return useMutation<MessageResponse, unknown, GameSnapshotRequest>({
    mutationFn: (data) => postFn(postCustomSnapshot, data),
    ...options,
    onSuccess: (...response) => {
      queryClient.invalidateQueries({ queryKey: snapshots.all.queryKey });
      queryClient.invalidateQueries({ queryKey: ['snapshots', 'list'] });
      options?.onSuccess?.(...response);
    },
  });
};

// Spawn game from replay
export const postSpawnGame = '/games/spawn';

export interface SpawnGameRequest {
  source: 'snapshot' | 'game';
  sourceId: string;
  eventIndex: number;
  controlPlayerIndex: number;
}

export interface SpawnGameResponse {
  gameId: string;
}

export const useSpawnGame = (
  options?: UseMutationOptions<SpawnGameResponse, unknown, SpawnGameRequest>,
) =>
  useMutation<SpawnGameResponse, unknown, SpawnGameRequest>({
    mutationFn: (data) => postFn(postSpawnGame, data),
    ...options,
  });
